import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLearner } from '../../hooks/useLearner';
import { useCurriculumCharacters } from '../../hooks/useCurriculumCharacters';
import { useChildWorksheets } from '../../hooks/useChildWorksheets';
import useFocusOnMount from '../../hooks/useFocusOnMount';
import useMediaQuery from '../../hooks/useMediaQuery';
import { CHARACTER_STATE_COLORS } from '../../utils/stateHelpers';
import BottomNav from '../../components/common/BottomNav';
import NavTabSelector from '../../components/common/NavTabSelector';
import CloudBand from '../../components/common/CloudBand';
import Drawer from '../../components/common/Drawer';
import CenterModal from '../../components/common/CenterModal';
import CharacterProfileContent from './CharacterProfileContent';
import WorksheetPreviewContent from './WorksheetPreviewContent';
import CreateWorksheetFlow from './CreateWorksheetFlow';
import styles from './WorkbookPage.module.css';

function useTabs() {
  const { t } = useTranslation();
  return [
    { key: 'characters', label: t('workbook.charactersTab') },
    { key: 'worksheets', label: t('workbook.worksheetsTab') },
    { key: 'practice', label: 'Practice' },
  ];
}

function useFilterOptions() {
  const { t } = useTranslation();
  return [
    { key: 'all', label: t('workbook.all') },
    { key: 'to_learn', label: t('common.new') },
    { key: 'practicing', label: t('common.practicing') },
    { key: 'known', label: t('common.known') },
  ];
}

/* Small status icon badge — top-right of character card.
   Each state gets a distinct icon per Figma node 54:77:
   known → checkmark, practicing → graduation cap, to_learn → star */
function StatusBadge({ status }) {
  const color = CHARACTER_STATE_COLORS[status] || CHARACTER_STATE_COLORS.to_learn;

  let icon;
  if (status === 'known') {
    icon = (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path d="M1 4L3.5 6.5L9 1" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  } else if (status === 'practicing') {
    icon = (
      <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
        <path d="M14.4885 4.07154V8.35718M3.29702 5.85722V8.35718C3.29702 8.92549 3.73918 9.47053 4.52624 9.87238C5.31329 10.2742 6.38077 10.5 7.49383 10.5C8.60689 10.5 9.67437 10.2742 10.4614 9.87238C11.2485 9.47053 11.6906 8.92549 11.6906 8.35718V5.85722M14.0829 4.73018C14.2081 4.67377 14.3143 4.58109 14.3885 4.46362C14.4626 4.34614 14.5014 4.20904 14.5 4.06929C14.4985 3.92954 14.457 3.79329 14.3805 3.6774C14.304 3.56152 14.1959 3.4711 14.0696 3.41735L8.07442 0.628824C7.89216 0.543932 7.69418 0.5 7.49386 0.5C7.29354 0.5 7.09556 0.543932 6.9133 0.628824L0.918857 3.41449C0.794329 3.47018 0.688393 3.56173 0.614005 3.67793C0.539617 3.79412 0.5 3.92994 0.5 4.06876C0.5 4.20759 0.539617 4.34341 0.614005 4.4596C0.688393 4.5758 0.794329 4.66734 0.918857 4.72304L6.9133 7.51442C7.09556 7.59931 7.29354 7.64324 7.49386 7.64324C7.69418 7.64324 7.89216 7.59931 8.07442 7.51442L14.0829 4.73018Z" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  } else {
    icon = (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M6 1L7.3 4.2L10.8 4.5L8.1 6.7L8.9 10L6 8.2L3.1 10L3.9 6.7L1.2 4.5L4.7 4.2Z" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <div
      className={styles.statusBadge}
      style={{ border: `1px solid ${color}` }}
    >
      {icon}
    </div>
  );
}

function CharacterCard({ character, onClick }) {
  const state = character.state || null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={styles.characterCard}
      aria-label={`${character.char}${character.pinyin ? ' ' + character.pinyin : ''}${character.meaning ? ' - ' + character.meaning : ''}`}
    >
      {state && <StatusBadge status={state} />}
      <span className={styles.characterChar}>
        {character.char}
      </span>
      {character.pinyin && (
        <span className={styles.characterPinyin}>
          {character.pinyin}
        </span>
      )}
      {character.meaning && (
        <span className={styles.characterMeaning}>
          {character.meaning}
        </span>
      )}
    </button>
  );
}

function useWorksheetTypeLabels() {
  const { t } = useTranslation();
  return {
    stroke_practice: t('workbook.writingPractice'),
    heuristic: t('workbook.mnemonic'),
    mixed: t('workbook.mixedPractice'),
  };
}

function WorksheetCard({ worksheet, onClick }) {
  const { t } = useTranslation();
  const worksheetTypeLabels = useWorksheetTypeLabels();
  const date = worksheet.created_at
    ? new Date(worksheet.created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    : '';
  const charCount =
    worksheet.character_ids?.length || worksheet.character_count || worksheet.characters?.length || 0;
  const typeLabel = worksheetTypeLabels[worksheet.worksheet_type] || t('workbook.practice');
  const displayName = worksheet.display_name || `${typeLabel} Worksheet`;
  const charTiles = worksheet.characters || [];

  return (
    <button
      type="button"
      onClick={onClick}
      className={styles.worksheetCard}
    >
      <div className={styles.worksheetCardBody}>
        <p className={styles.worksheetCardTitle}>
          {displayName}
        </p>
        <p className={styles.worksheetCardMeta}>
          {typeLabel} · {charCount} characters{date ? ` · ${date}` : ''}
        </p>
        {charTiles.length > 0 && (
          <div className={styles.worksheetCardTiles}>
            {charTiles.map((c) => (
              <div
                key={c.id || c.char}
                className={styles.worksheetCardTile}
              >
                {c.char || c.simplified_char}
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Chevron */}
      <svg
        width="8"
        height="14"
        viewBox="0 0 8 14"
        fill="none"
        className={styles.worksheetCardChevron}
        aria-hidden="true"
      >
        <path
          d="M1 1L7 7L1 13"
          stroke="#766855"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

export default function WorkbookPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: activeLearner } = useLearner();
  const mainRef = useFocusOnMount();
  const isDesktop = useMediaQuery('(min-width: 1200px)');
  const [drawerChar, setDrawerChar] = useState(null);
  const [drawerWorksheet, setDrawerWorksheet] = useState(null);
  const [worksheetModalOpen, setWorksheetModalOpen] = useState(false);
  const [worksheetModalInitial, setWorksheetModalInitial] = useState(null);

  const TABS = useTabs();
  const FILTER_OPTIONS = useFilterOptions();

  const [activeTab, setActiveTab] = useState('characters');
  const [filter, setFilter] = useState('all');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    if (!isDesktop) {
      setDrawerChar(null);
      setDrawerWorksheet(null);
    }
  }, [isDesktop]);

  useEffect(() => {
    if (location.state?.worksheetModal && isDesktop) {
      setWorksheetModalInitial(location.state.worksheetModal);
      setWorksheetModalOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, isDesktop]);

  const learnerId = activeLearner?.id;
  const { data: characters = [], isLoading: charsLoading } = useCurriculumCharacters(learnerId);
  const { data: worksheets = [], isLoading: worksheetsLoading } = useChildWorksheets(learnerId);
  const loading = charsLoading || worksheetsLoading;

  const filteredCharacters =
    filter === 'all'
      ? characters
      : characters.filter((c) => c.state === filter);

  // Pair characters into rows of 2
  const rows = [];
  for (let i = 0; i < filteredCharacters.length; i += 2) {
    rows.push(filteredCharacters.slice(i, i + 2));
  }

  const activeFilterLabel =
    FILTER_OPTIONS.find((o) => o.key === filter)?.label || t('workbook.all');

  return (
    <main className={styles.page} ref={mainRef} tabIndex={-1}>
      <CloudBand variant="teal" className={styles.cloudBand} />
      {/* Content */}
      <div className={styles.content}>
        {/* Page header — title + tab nav (+ CTA on worksheets tab) inline at desktop */}
        <div className={styles.pageHeader}>
          <h1 className={styles.title}>
            {t('workbook.title')}
          </h1>
          <div className={styles.tabWrapper}>
            <NavTabSelector
              tabs={TABS}
              active={activeTab}
              onChange={(key) => {
                if (key === 'practice') {
                  navigate('/practice');
                  return;
                }
                setActiveTab(key);
              }}
              className={styles.tabSelectorWrap}
            />
          </div>
          {activeTab === 'worksheets' && (
            <button
              type="button"
              onClick={() => {
                if (isDesktop) {
                  setWorksheetModalInitial(null);
                  setWorksheetModalOpen(true);
                } else {
                  navigate('/workbook/select-characters');
                }
              }}
              className={styles.ctaButton}
            >
              {t('workbook.createWorksheet')}
            </button>
          )}
        </div>

        {/* Characters tab */}
        {activeTab === 'characters' && (
          <>
            {/* Dropdown filter */}
            <div className={styles.dropdownWrapper}>
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { setDropdownOpen(false); }
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDropdownOpen((prev) => !prev); }
                }}
                className={styles.dropdownButton}
                aria-expanded={dropdownOpen}
                aria-haspopup="listbox"
              >
                <span className={styles.dropdownButtonLabel}>
                  {activeFilterLabel}
                </span>
                {/* Chevron down */}
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  className={`${styles.dropdownChevron}${dropdownOpen ? ` ${styles.dropdownChevronOpen}` : ''}`}
                >
                  <path
                    d="M1 1L5 5L9 1"
                    stroke="#211e1a"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Dropdown options */}
              {dropdownOpen && (
                <div className={styles.dropdownMenu} role="listbox">
                  {FILTER_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      role="option"
                      aria-selected={filter === opt.key}
                      onClick={() => {
                        setFilter(opt.key);
                        setDropdownOpen(false);
                      }}
                      className={`${styles.dropdownOption}${filter === opt.key ? ` ${styles.dropdownOptionActive}` : ''}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Character grid */}
            <div className={styles.characterGrid}>
              {loading && (
                <div className={styles.skeletonGrid} aria-live="polite">
                  {(isDesktop ? [0, 1, 2, 3, 4] : [0, 1, 2, 3]).map((i) => (
                    <div key={i} className={`skeleton ${styles.skeletonCharCard}`} />
                  ))}
                </div>
              )}

              {!loading && filteredCharacters.length === 0 && (
                <div className={styles.emptyState}>
                  <img
                    className={styles.emptyIllustration}
                    src="/assets/workbook-empty-characters.webp"
                    alt="Míng, Yǒng, and Lìng waiting for you"
                  />
                  <p className={styles.emptyTitle}>
                    {t('workbook.noCharacters')}
                  </p>
                  <p className={styles.emptySubtitle}>
                    {t('workbook.readStories')}
                  </p>
                </div>
              )}

              {!loading &&
                rows.map((row, i) => (
                  <div key={i} className={styles.characterRow}>
                    {row.map((char, j) => (
                      <CharacterCard
                        key={char.id || char.char || j}
                        character={char}
                        onClick={() => {
                          if (isDesktop) {
                            setDrawerChar(char);
                          } else {
                            navigate(
                              `/workbook/character/${encodeURIComponent(char.char)}`,
                              { state: { character: char } }
                            );
                          }
                        }}
                      />
                    ))}
                    {row.length === 1 && <div className={styles.characterSpacer} />}
                  </div>
                ))}
            </div>
          </>
        )}

        {/* Worksheets tab */}
        {activeTab === 'worksheets' && (
          <div className={styles.worksheetsTab}>
            {loading && (
              <div className={styles.skeletonWorksheetList} aria-live="polite">
                {(isDesktop ? [0, 1, 2, 3, 4, 5] : [0, 1, 2]).map((i) => (
                  <div key={i} className={`skeleton ${styles.skeletonWorksheetCard}`} />
                ))}
              </div>
            )}

            {!loading && worksheets.length === 0 && (
              <div className={styles.emptyCard}>
                <p className={styles.emptyTitle}>
                  {t('workbook.noWorksheets')}
                </p>
                <p className={styles.emptySubtitle}>
                  {t('workbook.createWorksheetHint')}
                </p>
              </div>
            )}

            {!loading && worksheets.length > 0 && (
              <div className={styles.worksheetList}>
                {worksheets.map((ws, i) => (
                  <WorksheetCard
                    key={ws.id || ws.worksheet_id || i}
                    worksheet={ws}
                    onClick={() => {
                      if (isDesktop) {
                        setDrawerWorksheet(ws);
                      } else {
                        navigate(`/workbook/preview/${ws.id || ws.worksheet_id}`);
                      }
                    }}
                  />
                ))}
              </div>
            )}

          </div>
        )}
      </div>

      <Outlet />
      <BottomNav active="practice" />

      <Drawer isOpen={!!drawerChar} onClose={() => setDrawerChar(null)}>
        {drawerChar && (
          <CharacterProfileContent
            character={drawerChar}
            onClose={() => setDrawerChar(null)}
          />
        )}
      </Drawer>

      <Drawer isOpen={!!drawerWorksheet} onClose={() => setDrawerWorksheet(null)}>
        {drawerWorksheet && (
          <WorksheetPreviewContent
            worksheet={drawerWorksheet}
            onClose={() => setDrawerWorksheet(null)}
          />
        )}
      </Drawer>

      <CenterModal isOpen={worksheetModalOpen} onClose={() => {
        setWorksheetModalOpen(false);
        setWorksheetModalInitial(null);
      }}>
        <CreateWorksheetFlow
          initialCharacterIds={worksheetModalInitial?.characterIds}
          initialStep={worksheetModalInitial?.step}
          isModal={true}
          onClose={() => {
            setWorksheetModalOpen(false);
            setWorksheetModalInitial(null);
          }}
          onComplete={(worksheet) => {
            setWorksheetModalOpen(false);
            setWorksheetModalInitial(null);
            queryClient.invalidateQueries({ queryKey: ['childWorksheets'] });
            queryClient.invalidateQueries({ queryKey: ['childCharacters'] });
            queryClient.invalidateQueries({ queryKey: ['curriculumCharacters'] });
            queryClient.invalidateQueries({ queryKey: ['learnerProfile'] });
            setDrawerWorksheet(worksheet);
          }}
        />
      </CenterModal>
    </main>
  );
}
